import React from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/athletesApi';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/lib/utils';
import { ArrowLeft, Calendar, MapPin, Award, Snowflake } from 'lucide-react';
import MedalDisplay from '../Components/athletes/MedalDisplay';
import OlympicTimeline from '../Components/athletes/OlympicTimeline';
import { Skeleton } from '../Components/ui/skeleton';
import { useLanguage } from '../src/context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../Components/ui/dropdown-menu";

export default function AthleteDetail() {
  const { t, setLanguage, language } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const athleteId = urlParams.get('id');

  const { data: athlete, isLoading } = useQuery({
    queryKey: ['athlete', athleteId, language],
    queryFn: async () => {
      const athletes = await base44.entities.Athlete.filter({ id: athleteId }, language);
      return athletes[0];
    },
    enabled: !!athleteId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628] p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="w-32 h-8 bg-[#1a365d] mb-8" />
          <div className="flex flex-col md:flex-row gap-8">
            <Skeleton className="w-48 h-48 rounded-full bg-[#1a365d] mx-auto md:mx-0" />
            <div className="flex-1 space-y-4">
              <Skeleton className="w-64 h-10 bg-[#1a365d]" />
              <Skeleton className="w-32 h-6 bg-[#1a365d]" />
              <Skeleton className="w-full h-24 bg-[#1a365d]" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <Snowflake className="w-16 h-16 text-blue-400 mx-auto mb-4 animate-spin" style={{ animationDuration: '3s' }} />
          <p className="text-white text-lg">{t('detail.notFound')}</p>
          <Link 
            to={createPageUrl('Home')}
            className="text-blue-400 hover:text-blue-300 mt-4 inline-block"
          >
            {t('detail.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  let birthDate = null;
  if (athlete.birth_date) {
    const dateObj = new Date(athlete.birth_date);
    // Check if date is valid
    if (!isNaN(dateObj.getTime())) {
      birthDate = dateObj.toLocaleDateString(language === 'de' ? 'de-DE' : language === 'en' ? 'en-US' : 'it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } else {
      // If parsing fails, assume it's already a localized string from i18n
      birthDate = athlete.birth_date;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628]">
      <div className="fixed top-4 right-4 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger className="p-2 rounded-full hover:bg-white/10 transition-colors outline-none">
            <img 
              src="/resources/icons8-world-50.png" 
              alt="Switch Language" 
              className="w-8 h-8 opacity-80 hover:opacity-100 transition-opacity"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0f2847] border-blue-500/30 text-white">
            <DropdownMenuItem 
              onClick={() => setLanguage('it')}
              className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 ${language === 'it' ? 'text-blue-400 font-bold' : ''}`}
            >
              Italiano
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setLanguage('en')}
              className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 ${language === 'en' ? 'text-blue-400 font-bold' : ''}`}
            >
              English
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setLanguage('de')}
              className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 ${language === 'de' ? 'text-blue-400 font-bold' : ''}`}
            >
              Deutsch
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -right-20 w-96 h-96 rounded-full bg-blue-500/5 blur-3xl" />
        <div className="absolute bottom-1/4 -left-20 w-96 h-96 rounded-full bg-[#d4af37]/5 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8 md:py-12">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Link 
            to={createPageUrl('Home')}
            className="inline-flex items-center gap-2 text-blue-300 hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm tracking-wider uppercase">{t('detail.allAthletes')}</span>
          </Link>
        </motion.div>

        {/* Hero Section */}
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 mb-12">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="flex-shrink-0"
          >
            <div className="relative w-48 h-48 md:w-56 md:h-56 mx-auto md:mx-0 rounded-full overflow-hidden border-4 border-[#1a365d]">
                {athlete.photo_url ? (
                  <img
                    src={athlete.photo_url}
                    alt={athlete.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1a365d] to-[#0a1628] flex items-center justify-center">
                    <span className="text-6xl font-light text-blue-300">
                      {athlete.name?.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex-1 text-center md:text-left"
          >
            <h1 className="text-3xl md:text-5xl font-light text-white tracking-wide mb-2">
              {athlete.name}
            </h1>
            <p className="text-blue-400 text-lg tracking-widest uppercase mb-6">
              {athlete.sport}
            </p>

            {/* Medals */}
            <div className="mb-6">
              <MedalDisplay medals={athlete.medals} />
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap gap-4 justify-center md:justify-start text-sm text-blue-200">
              {birthDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{birthDate}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{athlete.birthPlace}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Biography */}
        {/*athlete.biography && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <h2 className="text-xl font-light text-white tracking-wider mb-4 flex items-center gap-3">
              <div className="w-8 h-px bg-blue-400" />
              {t('detail.biography')}
            </h2>
            <div className="bg-[#0f2847]/30 backdrop-blur-sm rounded-2xl p-6 border border-blue-900/30">
              <p className="text-blue-100 leading-relaxed whitespace-pre-line">
                {athlete.biography}
              </p>
            </div>
          </motion.section>
        )*/}

        {/* Curiosities */}
        {athlete.curiosities && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <h2 className="text-xl font-light text-white tracking-wider mb-4 flex items-center gap-3">
              <div className="w-8 h-px bg-[#d4af37]" />
              {t('detail.curiosities')}
            </h2>
            <div className="bg-gradient-to-br from-[#0f2847]/50 to-[#1a365d]/30 backdrop-blur-sm rounded-2xl p-6 border border-[#d4af37]/20">
              {Array.isArray(athlete.curiosities) ? (
                <div className="space-y-4">
                  {athlete.curiosities.map((curiosity, index) => (
                    <div key={index}>
                      {curiosity.title && (
                        <h3 className="text-[#d4af37] font-medium mb-1">
                          {curiosity.title}
                        </h3>
                      )}
                      <p className="text-blue-100 leading-relaxed italic opacity-90">
                        {curiosity.text || curiosity}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-blue-100 leading-relaxed italic">
                  &quot;{athlete.curiosities}&quot;
                </p>
              )}
            </div>
          </motion.section>
        )}

        {/* Olympic Games */}
        {athlete.olympic_games && athlete.olympic_games.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-12"
          >
            <h2 className="text-xl font-light text-white tracking-wider mb-6 flex items-center gap-3">
              <div className="w-8 h-px bg-[#d4af37]" />
              {t('detail.olympicParticipations')}
            </h2>
            <OlympicTimeline games={athlete.olympic_games} />
          </motion.section>
        )}

        {/* Achievements */}
        {athlete.achievements && athlete.achievements.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-xl font-light text-white tracking-wider mb-4 flex items-center gap-3">
              <div className="w-8 h-px bg-blue-400" />
              {t('detail.otherResults')}
            </h2>
            <div className="grid gap-3">
              {athlete.achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-start gap-3 bg-[#0f2847]/30 rounded-xl p-4 border border-blue-900/30"
                >
                  <Award className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                  <span className="text-blue-100">{achievement}</span>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-16 text-center"
        >
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent mx-auto mb-4" />
          <p className="text-blue-300/50 text-xs tracking-widest uppercase">
            {t('detail.footer')}
          </p>
        </motion.footer>
      </div>
    </div>
  );
}